import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';

type Settings = { names: Record<string, string>; revision: number };
export function HotelAccessNames({
  agencyId,
  connectionId,
  codes,
}: {
  agencyId: number;
  connectionId: string;
  codes: string[];
}) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Settings | null>(null);
  const key = ['hotel-access-names', agencyId, connectionId];
  const path = `agencies/${agencyId}/hotels/connector-accounts/${encodeURIComponent(connectionId)}/display-names`;
  const query = useQuery(key, async () => (await client.get<Settings>(path)).data, { enabled: open, retry: false });
  const current = draft ?? query.data;
  const save = useMutation(
    async () => {
      if (!current) throw new Error('Missing display names');
      await client.put(path, {
        revision: current.revision,
        names: Object.fromEntries(
          codes.map((code) => [
            code,
            Object.prototype.hasOwnProperty.call(current.names, code) ? current.names[code] : '',
          ]),
        ),
      });
    },
    {
      onSuccess: async () => {
        setOpen(false);
        setDraft(null);
        await Promise.all([
          cache.invalidateQueries(key),
          cache.invalidateQueries(['hotel-connections', agencyId]),
          cache.invalidateQueries(['hotel-search-policy', agencyId]),
        ]);
      },
    },
  );
  return (
    <>
      <Button
        disabled={!codes.length}
        onClick={() => {
          setDraft(null);
          save.reset();
          setOpen(true);
        }}
      >
        {t('hotelAccessNames.manage')}
      </Button>
      <Dialog open={open} fullWidth maxWidth="sm" onClose={() => !save.isLoading && setOpen(false)}>
        <DialogTitle>{t('hotelAccessNames.manage')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={2}>
            <Typography>{t('hotelAccessNames.hint')}</Typography>
            {query.isError && <Alert severity="error">{t('hotelConnections.error')}</Alert>}
            {current &&
              !query.isError &&
              codes.map((code) => (
                <TextField
                  key={code}
                  label={t('hotelPolicy.accessIdentity', { code })}
                  helperText={t('hotelAccessNames.optional')}
                  value={Object.prototype.hasOwnProperty.call(current.names, code) ? current.names[code] : ''}
                  inputProps={{ maxLength: 200 }}
                  sx={{ '& input': { fontSize: '16px' } }}
                  disabled={save.isLoading || query.isFetching}
                  onChange={(event) =>
                    setDraft({ ...current, names: { ...current.names, [code]: event.target.value } })
                  }
                />
              ))}
            {save.isError && <Alert severity="error">{t('hotelAccessNames.saveError')}</Alert>}
            {(query.isError || save.isError) && (
              <Button
                disabled={save.isLoading}
                onClick={() => {
                  setDraft(null);
                  save.reset();
                  void query.refetch();
                }}
              >
                {t('hotelConnections.retry')}
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isLoading} onClick={() => setOpen(false)}>
            {t('hotelReservation.close')}
          </Button>
          <Button
            disabled={!draft || query.isFetching || query.isError || save.isLoading || save.isError}
            onClick={() => save.mutate()}
          >
            {t('hotelPolicy.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
